import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';

interface TaskPresentation {
    icon: string;
    variant: 'read' | 'write' | 'delete' | 'template' | 'unknown';
    label: string;
    tooltip: string;
}

/**
 * The kind of operation a kørsel performs.
 *
 * `QueueRequest.Task` is the read/write/delete discriminator — `Action` only says whether the request
 * is a data synchronization or a template lookup. Both are forced lowercase by the synchronization
 * tool's request validation, but they are free `nvarchar` columns with no constraint, so rows written
 * before that validation existed can hold anything: hence the case-insensitive match and the fallback.
 *
 * Three icons, not five. `write`, `create` and `update` share one — they run through the same handler
 * and reading the list is a scanning task, not a study. The distinction they really carry (create
 * fails if the row exists, update fails if it does not, write upserts) lives in the tooltip, where it
 * costs nothing to anyone who is not asking.
 */
@Component({
    selector: 'app-task-badge',
    templateUrl: './task-badge.component.html',
    styleUrl: './task-badge.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgbTooltip]
})
export class TaskBadgeComponent {
    readonly action = input('');
    readonly task = input('');

    protected readonly presentation = computed<TaskPresentation>(() => {
        const task = this.task().trim().toLowerCase();
        const action = this.action().trim().toLowerCase();

        switch (task) {
            case 'read':
                return {
                    icon: 'fa-eye',
                    variant: 'read',
                    label: 'Læs',
                    tooltip: 'Læs'
                };
            case 'update':
            case 'create':
            case 'write':
                return {
                    icon: 'fa-pencil',
                    variant: 'write',
                    label: 'Skriv',
                    tooltip: 'Skriv'
                };
            case 'delete':
                return {
                    icon: 'fa-trash',
                    variant: 'delete',
                    label: 'Slet',
                    tooltip: 'Slet'
                };
            case 'list':
            case 'detail':
                return {
                    icon: 'fa-file-lines',
                    variant: 'template',
                    label: 'Skabelon',
                    tooltip: 'Skabelondata — ikke en datasynkronisering'
                };
            default:
                return {
                    icon: 'fa-circle-question',
                    variant: 'unknown',
                    label: task || 'Ukendt',
                    // Show the raw values rather than inventing a meaning for them.
                    tooltip: task ? `Ukendt handling: ${action} / ${task}` : 'Handling ikke angivet'
                };
        }
    });
}
